import React, { useState, useEffect, useCallback, Fragment } from 'react';
import {
  FiFileText,
  FiSearch,
  FiFilter,
  FiRefreshCw,
  FiCheckCircle,
  FiXCircle,
  FiClock,
  FiUser,
  FiCalendar,
  FiAlertCircle,
  FiHome,
  FiEye
} from 'react-icons/fi';
import { Dialog, Transition } from '@headlessui/react';
import { ExclamationTriangleIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { AdminLayout } from '../../components/admin';
import { useAdminAuth } from '../../hooks/admin/useAdminAuth';
import { useErrorHandler } from '../../hooks/useErrorHandler';
import ViewClaimDetailsModal from '../../components/admin/claims/ViewClaimDetailsModal';
import { useDialog } from '../../hooks/useDialog';
import toast from 'react-hot-toast';
import { API_URL } from '../../services/api';

// Types
// BUG-003 Debug: ViewClaimDetailsModal fix
interface Claim {
  id: string;
  userId: string;
  companyId: string;
  status: 'PENDING' | 'APPROVED' | 'REJECTED';
  isPrimary: boolean;
  canEdit: boolean;
  canDelete: boolean;
  canTransfer: boolean;
  isApproved: boolean;
  isActive: boolean;
  approvedBy?: string;
  approvedAt?: string;
  notes?: string;
  createdAt: string;
  updatedAt: string;
  user?: {
    id: string;
    email: string;
    firstName?: string;
    lastName?: string;
    role: string;
  };
  company?: {
    id: string;
    slug: string;
    nameTh: string;
    nameEn?: string;
    province?: string;
  };
  approver?: {
    id: string;
    email: string;
    firstName?: string;
    lastName?: string;
    role: string;
  };
}

interface ClaimsResponse {
  claims: Claim[];
  total: number;
  pending: number;
  approved: number;
  rejected: number;
}

const ClaimsManagement: React.FC = () => {
  const { token } = useAdminAuth();
  const [errorState, { handleError, clearError }] = useErrorHandler();
  const { showConfirm, ConfirmDialog, AlertDialog } = useDialog();

  // Custom API call function
  const callApi = useCallback(async <T = any>(options: {
    method: 'GET' | 'POST' | 'PUT' | 'DELETE';
    endpoint: string;
    data?: any;
  }): Promise<{ success: boolean; data: T; message?: string }> => {
    if (!token) {
      throw new Error('ไม่พบ token สำหรับการยืนยันตัวตน');
    }

    
    const response = await fetch(`${API_URL}${options.endpoint}`, {
      method: options.method,
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
      },
      body: options.data ? JSON.stringify(options.data) : undefined,
    });

    const result = await response.json();

    if (!response.ok) {
      throw new Error(result.message || result.detail || `HTTP ${response.status}`);
    }

    return {
      success: true,
      data: result.data || result,
      message: result.message
    };
  }, [token]);

  // State
  const [claims, setClaims] = useState<Claim[]>([]);
  const [loading, setLoading] = useState(true);
  const [totalClaims, setTotalClaims] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [statusFilter, setStatusFilter] = useState<'all' | 'pending' | 'approved' | 'rejected'>('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [actionLoading, setActionLoading] = useState<string | null>(null);
  const [selectedClaim, setSelectedClaim] = useState<Claim | null>(null);
  const [showDetailsModal, setShowDetailsModal] = useState(false);

  // Reject modal state
  const [rejectModalOpen, setRejectModalOpen] = useState(false);
  const [selectedRejectClaim, setSelectedRejectClaim] = useState<{id: string, name: string} | null>(null);
  const [rejectReason, setRejectReason] = useState('');
  const [rejectError, setRejectError] = useState('');
  const [rejectLoading, setRejectLoading] = useState(false);

  // Load claims
  const loadClaims = useCallback(async (page = 1) => {
    console.log('🔍 loadClaims called - page:', page, 'statusFilter:', statusFilter, 'token:', token ? 'exists' : 'missing');
    try {
      setLoading(true);
      clearError();

      const params = new URLSearchParams({
        page: page.toString(),
        per_page: '20',
        ...(statusFilter !== 'all' && { status: statusFilter.toUpperCase() })
      });

      console.log('🌐 API Request:', `/api/admin/claims?${params.toString()}`);

      const response = await callApi<ClaimsResponse>({
        method: 'GET',
        endpoint: `/api/admin/claims?${params.toString()}`
      });

      console.log('✅ API Response:', response);

      if (response.success) {
        setClaims(response.data.claims || []);
        setTotalClaims(response.data.total || 0);
        setCurrentPage(page);
        // Calculate total pages (using 20 items per page)
        const totalPages = Math.ceil((response.data.total || 0) / 20);
        setTotalPages(totalPages);
      }
    } catch (error) {
      handleError(error, 'ไม่สามารถโหลดรายการคำขอได้');
      setClaims([]);
    } finally {
      setLoading(false);
    }
  }, [statusFilter, token, callApi, handleError, clearError]);

  // Initial load
  useEffect(() => {
    console.log('🚀 useEffect triggered - token:', token ? 'exists' : 'missing');
    if (token) {
      console.log('✅ Calling loadClaims(1)...');
      loadClaims(1);
    } else {
      console.log('❌ No token, skipping loadClaims');
    }
  }, [loadClaims, token]);

  // Handle approve claim
  const handleApproveClaim = async (claimId: string, companyName: string) => {
    await showConfirm(
      {
        title: 'ยืนยันการอนุมัติ',
        message: `ต้องการอนุมัติคำขอสิทธิ์สำหรับบริษัท "${companyName}" หรือไม่?`,
        confirmText: 'อนุมัติ',
        cancelText: 'ยกเลิก',
        variant: 'success'
      },
      async () => {
        try {
          setActionLoading(claimId);

          // FIXED: ใช้ endpoint และ request body ที่ถูกต้องตาม backend API
          const requestBody = {
            notes: undefined // Optional: admin approval notes
          };

          const response = await callApi({
            method: 'PUT',
            endpoint: `/api/admin/claims/${claimId}/approve`,
            data: requestBody
          });

          if (response.success) {
            toast.success('อนุมัติคำขอเรียบร้อยแล้ว');
            loadClaims(currentPage);
          }
        } catch (error) {
          handleError(error, 'ไม่สามารถอนุมัติคำขอได้');
        } finally {
          setActionLoading(null);
        }
      }
    );
  };

  // Handle reject claim - เปิด modal
  const handleRejectClaim = (claimId: string, companyName: string) => {
    setSelectedRejectClaim({ id: claimId, name: companyName });
    setRejectReason('');
    setRejectError('');
    setRejectModalOpen(true);
  };

  // Handle reject submit
  const handleRejectSubmit = async () => {
    // Validate textarea
    if (!rejectReason.trim()) {
      setRejectError('กรุณาระบุเหตุผลในการปฏิเสธ');
      return;
    }

    if (!selectedRejectClaim) return;

    try {
      setRejectLoading(true);
      setRejectError('');

      // FIXED: ใช้ endpoint และ request body ที่ถูกต้องตาม backend API
      const response = await callApi({
        method: 'PUT',
        endpoint: `/api/admin/claims/${selectedRejectClaim.id}/reject`,
        data: {
          reason: rejectReason.trim() // REQUIRED field ตาม backend schema
        }
      });

      if (response.success) {
        toast.success('ปฏิเสธคำขอเรียบร้อยแล้ว');
        setRejectModalOpen(false);
        setSelectedRejectClaim(null);
        setRejectReason('');
        loadClaims(currentPage);
      }
    } catch (error) {
      handleError(error, 'ไม่สามารถปฏิเสธคำขอได้');
    } finally {
      setRejectLoading(false);
    }
  };

  // Handle view claim details
  const handleViewDetails = (claim: Claim) => {
    console.log('👁️ handleViewDetails called with claim:', claim);
    console.log('Setting selectedClaim and showDetailsModal to true');
    setSelectedClaim(claim);
    setShowDetailsModal(true);
    console.log('After setState - selectedClaim:', claim, 'showDetailsModal: true');
  };

  // Handle close details modal
  const handleCloseDetailsModal = () => {
    setShowDetailsModal(false);
    setSelectedClaim(null);
  };

  // Helper function to get status from isApproved and isActive
  const getClaimStatus = (claim: Claim): 'pending' | 'approved' | 'rejected' => {
    if (claim.isApproved && claim.isActive) {
      return 'approved';
    } else if (!claim.isApproved && claim.isActive === false) {
      return 'rejected';
    }
    return 'pending';
  };

  // Status badge component
  const StatusBadge: React.FC<{ claim: Claim }> = ({ claim }) => {
    const status = getClaimStatus(claim);
    const config = {
      pending: {
        color: 'bg-yellow-100 text-yellow-800',
        icon: <FiClock className="h-3 w-3 mr-1" />,
        label: 'รอดำเนินการ'
      },
      approved: {
        color: 'bg-green-100 text-green-800',
        icon: <FiCheckCircle className="h-3 w-3 mr-1" />,
        label: 'อนุมัติแล้ว'
      },
      rejected: {
        color: 'bg-red-100 text-red-800',
        icon: <FiXCircle className="h-3 w-3 mr-1" />,
        label: 'ปฏิเสธแล้ว'
      }
    };

    const statusConfig = config[status];

    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${statusConfig.color}`}>
        {statusConfig.icon}
        {statusConfig.label}
      </span>
    );
  };

  // Mobile Card Component
  const ClaimMobileCard: React.FC<{
    claim: Claim;
    onApprove: (id: string, name: string) => void;
    onReject: (id: string, name: string) => void;
    onViewDetails: (claim: Claim) => void;
  }> = ({ claim, onApprove, onReject, onViewDetails }) => {
    const status = getClaimStatus(claim);
    const companyName = claim.company?.nameTh || 'ไม่ระบุชื่อ';

    return (
      <div className="bg-white rounded-lg shadow p-4 border border-gray-200 hover:shadow-md transition-shadow">
        {/* Company */}
        <div className="mb-3">
          <div className="text-xs text-gray-500 mb-1">บริษัท</div>
          <div className="font-semibold text-gray-900 flex items-center">
            <FiHome className="h-4 w-4 text-gray-400 mr-2" />
            {companyName}
          </div>
          {claim.company?.nameEn && (
            <div className="text-sm text-gray-500 ml-6">{claim.company.nameEn}</div>
          )}
        </div>

        {/* User */}
        <div className="mb-3">
          <div className="text-xs text-gray-500 mb-1">ผู้ใช้</div>
          <div className="flex items-start">
            <FiUser className="h-4 w-4 text-gray-400 mr-2 mt-0.5" />
            <div>
              <div className="text-sm font-medium text-gray-900">
                {claim.user?.firstName && claim.user?.lastName
                  ? `${claim.user.firstName} ${claim.user.lastName}`
                  : 'ไม่ระบุชื่อ'}
              </div>
              <div className="text-sm text-gray-500">{claim.user?.email || 'ไม่มีอีเมล'}</div>
            </div>
          </div>
        </div>

        {/* Status Badge */}
        <div className="mb-3">
          <StatusBadge claim={claim} />
        </div>

        {/* Notes (ถ้ามี) */}
        <div className="mb-3">
          <div className="text-xs text-gray-500 mb-1">หมายเหตุ</div>
          <div className="text-sm text-gray-700">
            {status === 'rejected' && claim.rejectionReason ? (
              <span className="text-red-600">ถูกปฏิเสธ: {claim.rejectionReason}</span>
            ) : claim.notes ? (
              claim.notes
            ) : (
              <span className="text-gray-400 italic">ไม่มีหมายเหตุ</span>
            )}
          </div>
        </div>

        {/* Date */}
        <div className="mb-4 flex items-center text-sm text-gray-500">
          <FiCalendar className="h-4 w-4 mr-1" />
          {new Date(claim.createdAt).toLocaleDateString('th-TH', {
            year: 'numeric',
            month: 'short',
            day: 'numeric'
          })}
        </div>

        {/* Action Buttons - Full Width with Labels */}
        <div className="flex flex-col gap-2 pt-3 border-t border-gray-200">
          <button
            onClick={() => onViewDetails(claim)}
            className="w-full inline-flex items-center justify-center px-4 py-2 border border-blue-300 text-sm font-medium rounded-md text-blue-700 bg-blue-50 hover:bg-blue-100 transition-colors"
          >
            <FiEye className="mr-2 h-4 w-4" />
            ดูรายละเอียด
          </button>

          {status === 'pending' && (
            <>
              <button
                onClick={() => onApprove(claim.id, companyName)}
                disabled={actionLoading === claim.id}
                className="w-full inline-flex items-center justify-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-green-600 hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {actionLoading === claim.id ? (
                  <FiRefreshCw className="animate-spin h-4 w-4" />
                ) : (
                  <>
                    <FiCheckCircle className="mr-2 h-4 w-4" />
                    อนุมัติ
                  </>
                )}
              </button>
              <button
                onClick={() => onReject(claim.id, companyName)}
                disabled={actionLoading === claim.id}
                className="w-full inline-flex items-center justify-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-red-600 hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {actionLoading === claim.id ? (
                  <FiRefreshCw className="animate-spin h-4 w-4" />
                ) : (
                  <>
                    <FiXCircle className="mr-2 h-4 w-4" />
                    ปฏิเสธ
                  </>
                )}
              </button>
            </>
          )}
        </div>
      </div>
    );
  };

  // Filter claims based on search query
  const filteredClaims = claims.filter(claim => {
    if (!searchQuery) return true;

    const searchLower = searchQuery.toLowerCase();
    const companyNameTh = claim.company?.nameTh?.toLowerCase() || '';
    const companyNameEn = claim.company?.nameEn?.toLowerCase() || '';
    const userEmail = claim.user?.email?.toLowerCase() || '';
    const userName = `${claim.user?.firstName || ''} ${claim.user?.lastName || ''}`.toLowerCase();

    return companyNameTh.includes(searchLower) ||
           companyNameEn.includes(searchLower) ||
           userEmail.includes(searchLower) ||
           userName.includes(searchLower);
  });

  return (
    <AdminLayout
      title="จัดการคำขอสิทธิ์"
      breadcrumbs={[
        { label: 'แดชบอร์ด', href: '/admin/dashboard' },
        { label: 'จัดการคำขอสิทธิ์' }
      ]}
    >
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 flex items-center">
              <FiFileText className="mr-3 h-8 w-8 text-blue-600" />
              จัดการคำขอสิทธิ์
            </h1>
            <p className="mt-1 text-sm text-gray-600">
              จัดการคำขอสิทธิ์การเป็นเจ้าของบริษัท (รวม {totalClaims.toLocaleString()} รายการ)
            </p>
          </div>
          <div className="flex space-x-3">
            <button
              onClick={() => setShowFilters(!showFilters)}
              className="inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
            >
              <FiFilter className="mr-2 h-4 w-4" />
              ตัวกรอง
            </button>
            <button
              onClick={() => loadClaims(currentPage)}
              className="inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
            >
              <FiRefreshCw className="mr-2 h-4 w-4" />
              รีเฟรช
            </button>
          </div>
        </div>

        {/* Search and Filters */}
        <div className="space-y-4">
          {/* Search */}
          <div className="relative">
            <FiSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
            <input
              type="text"
              placeholder="ค้นหาด้วยชื่อบริษัท, อีเมลผู้ใช้, หรือชื่อผู้ใช้..."
              className="pl-10 pr-4 py-2 w-full border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>

          {/* Status Filter Tabs */}
          <div className="border-b border-gray-200">
            <nav className="-mb-px flex space-x-8">
              <button
                onClick={() => setStatusFilter('all')}
                className={`
                  whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm transition-colors
                  ${statusFilter === 'all'
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }
                `}
              >
                ทั้งหมด
                <span className="ml-2 py-0.5 px-2 rounded-full text-xs bg-gray-100 text-gray-900">
                  {totalClaims}
                </span>
              </button>
              <button
                onClick={() => setStatusFilter('pending')}
                className={`
                  whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm transition-colors inline-flex items-center
                  ${statusFilter === 'pending'
                    ? 'border-yellow-500 text-yellow-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }
                `}
              >
                <FiClock className="mr-1.5 h-4 w-4" />
                รอดำเนินการ
              </button>
              <button
                onClick={() => setStatusFilter('approved')}
                className={`
                  whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm transition-colors inline-flex items-center
                  ${statusFilter === 'approved'
                    ? 'border-green-500 text-green-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }
                `}
              >
                <FiCheckCircle className="mr-1.5 h-4 w-4" />
                อนุมัติแล้ว
              </button>
              <button
                onClick={() => setStatusFilter('rejected')}
                className={`
                  whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm transition-colors inline-flex items-center
                  ${statusFilter === 'rejected'
                    ? 'border-red-500 text-red-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }
                `}
              >
                <FiXCircle className="mr-1.5 h-4 w-4" />
                ปฏิเสธแล้ว
              </button>
            </nav>
          </div>

          {/* Advanced Filters (Optional) */}
          {showFilters && (
            <div className="bg-gray-50 p-4 rounded-lg">
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">สถานะ (Dropdown)</label>
                  <select
                    className="w-full border border-gray-300 rounded-md px-3 py-2 bg-white"
                    value={statusFilter}
                    onChange={(e) => setStatusFilter(e.target.value as any)}
                  >
                    <option value="all">ทั้งหมด</option>
                    <option value="pending">รอดำเนินการ</option>
                    <option value="approved">อนุมัติแล้ว</option>
                    <option value="rejected">ปฏิเสธแล้ว</option>
                  </select>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Error Display */}
        {errorState.hasError && errorState.error && (
          <div className="bg-red-50 border border-red-200 rounded-md p-4">
            <div className="flex">
              <FiAlertCircle className="h-5 w-5 text-red-400" />
              <div className="ml-3">
                <h3 className="text-sm font-medium text-red-800">เกิดข้อผิดพลาด</h3>
                <p className="mt-1 text-sm text-red-700">{errorState.error.message}</p>
              </div>
            </div>
          </div>
        )}

        {/* Claims Display - Dual View Pattern */}
        <div className="bg-white shadow rounded-lg overflow-hidden">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <FiRefreshCw className="animate-spin h-8 w-8 text-blue-600" />
              <span className="ml-2 text-gray-600">กำลังโหลด...</span>
            </div>
          ) : filteredClaims.length > 0 ? (
            <>
              {/* Mobile Card View - แสดงเมื่อ < 768px */}
              <div className="md:hidden space-y-4 p-4">
                {filteredClaims.map((claim) => (
                  <ClaimMobileCard
                    key={claim.id}
                    claim={claim}
                    onApprove={handleApproveClaim}
                    onReject={handleRejectClaim}
                    onViewDetails={handleViewDetails}
                  />
                ))}
              </div>

              {/* Desktop Table View - แสดงเมื่อ >= 768px */}
              <div className="hidden md:block overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        บริษัท
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        ผู้ใช้
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        สถานะ
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        หมายเหตุ
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        วันที่สร้าง
                      </th>
                      <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                        การจัดการ
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {filteredClaims.map((claim) => (
                      <tr key={claim.id} className="hover:bg-gray-50">
                        <td className="px-6 py-4">
                          <div className="flex items-center">
                            <FiHome className="h-5 w-5 text-gray-400 mr-2" />
                            <div>
                              <div className="text-sm font-medium text-gray-900">
                                {claim.company?.nameTh || 'ไม่ระบุชื่อ'}
                              </div>
                              {claim.company?.nameEn && (
                                <div className="text-sm text-gray-500">{claim.company.nameEn}</div>
                              )}
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          <div className="flex items-center">
                            <FiUser className="h-5 w-5 text-gray-400 mr-2" />
                            <div>
                              <div className="text-sm font-medium text-gray-900">
                                {claim.user?.firstName && claim.user?.lastName
                                  ? `${claim.user.firstName} ${claim.user.lastName}`
                                  : 'ไม่ระบุชื่อ'
                                }
                              </div>
                              <div className="text-sm text-gray-500">{claim.user?.email || 'ไม่มีอีเมล'}</div>
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <StatusBadge claim={claim} />
                        </td>
                        <td className="px-6 py-4">
                          <div className="text-sm text-gray-900 max-w-xs truncate">
                            {getClaimStatus(claim) === 'rejected' && claim.rejectionReason ? (
                              <span className="text-red-600">ถูกปฏิเสธ: {claim.rejectionReason}</span>
                            ) : claim.notes ? (
                              claim.notes
                            ) : (
                              <span className="text-gray-400 italic">ไม่มีหมายเหตุ</span>
                            )}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center text-sm text-gray-500">
                            <FiCalendar className="h-4 w-4 mr-1" />
                            {new Date(claim.createdAt).toLocaleDateString('th-TH', {
                              year: 'numeric',
                              month: 'short',
                              day: 'numeric'
                            })}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                          <div className="flex items-center justify-end space-x-2">
                            {/* ปุ่มดูรายละเอียด - แสดงเสมอ */}
                            <button
                              onClick={() => handleViewDetails(claim)}
                              className="inline-flex items-center px-3 py-1.5 border border-blue-300 text-xs font-medium rounded-md text-blue-700 bg-blue-50 hover:bg-blue-100 transition-colors"
                            >
                              <FiEye className="mr-1 h-4 w-4" />
                              ดูรายละเอียด
                            </button>

                            {/* ปุ่มอนุมัติ/ปฏิเสธ - แสดงเฉพาะ pending */}
                            {getClaimStatus(claim) === 'pending' && (
                              <>
                                <button
                                  onClick={() => handleApproveClaim(claim.id, claim.company?.nameTh || 'บริษัท')}
                                  disabled={actionLoading === claim.id}
                                  className="inline-flex items-center px-3 py-1.5 border border-transparent text-xs font-medium rounded-md text-white bg-green-600 hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed"
                                >
                                  {actionLoading === claim.id ? (
                                    <FiRefreshCw className="animate-spin h-4 w-4" />
                                  ) : (
                                    <>
                                      <FiCheckCircle className="mr-1 h-4 w-4" />
                                      อนุมัติ
                                    </>
                                  )}
                                </button>
                                <button
                                  onClick={() => handleRejectClaim(claim.id, claim.company?.nameTh || 'บริษัท')}
                                  disabled={actionLoading === claim.id}
                                  className="inline-flex items-center px-3 py-1.5 border border-transparent text-xs font-medium rounded-md text-white bg-red-600 hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed"
                                >
                                  {actionLoading === claim.id ? (
                                    <FiRefreshCw className="animate-spin h-4 w-4" />
                                  ) : (
                                    <>
                                      <FiXCircle className="mr-1 h-4 w-4" />
                                      ปฏิเสธ
                                    </>
                                  )}
                                </button>
                              </>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Pagination */}
              {totalPages > 1 && (
                <div className="bg-white px-4 py-3 flex items-center justify-between border-t border-gray-200 sm:px-6">
                  <div className="flex-1 flex justify-between sm:hidden">
                    <button
                      onClick={() => loadClaims(currentPage - 1)}
                      disabled={currentPage === 1}
                      className="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
                    >
                      ก่อนหน้า
                    </button>
                    <button
                      onClick={() => loadClaims(currentPage + 1)}
                      disabled={currentPage === totalPages}
                      className="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
                    >
                      ถัดไป
                    </button>
                  </div>
                  <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
                    <div>
                      <p className="text-sm text-gray-700">
                        แสดง <span className="font-medium">{((currentPage - 1) * 20) + 1}</span> ถึง{' '}
                        <span className="font-medium">{Math.min(currentPage * 20, totalClaims)}</span> จาก{' '}
                        <span className="font-medium">{totalClaims}</span> รายการ
                      </p>
                    </div>
                    <div>
                      <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px">
                        <button
                          onClick={() => loadClaims(currentPage - 1)}
                          disabled={currentPage === 1}
                          className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50"
                        >
                          ก่อนหน้า
                        </button>
                        {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                          const page = Math.max(1, Math.min(totalPages - 4, currentPage - 2)) + i;
                          return (
                            <button
                              key={page}
                              onClick={() => loadClaims(page)}
                              className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium ${
                                page === currentPage
                                  ? 'z-10 bg-blue-50 border-blue-500 text-blue-600'
                                  : 'bg-white border-gray-300 text-gray-500 hover:bg-gray-50'
                              }`}
                            >
                              {page}
                            </button>
                          );
                        })}
                        <button
                          onClick={() => loadClaims(currentPage + 1)}
                          disabled={currentPage === totalPages}
                          className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50"
                        >
                          ถัดไป
                        </button>
                      </nav>
                    </div>
                  </div>
                </div>
              )}
            </>
          ) : (
            <div className="text-center py-12">
              <FiFileText className="mx-auto h-12 w-12 text-gray-400" />
              <h3 className="mt-2 text-sm font-medium text-gray-900">ไม่พบคำขอ</h3>
              <p className="mt-1 text-sm text-gray-500">
                {searchQuery ? 'ลองปรับเปลี่ยนคำค้นหา' : 'ยังไม่มีคำขอสิทธิ์ในระบบ'}
              </p>
            </div>
          )}
        </div>
      </div>

      {/* View Claim Details Modal */}
      <ViewClaimDetailsModal
        claim={selectedClaim}
        isOpen={showDetailsModal}
        onClose={handleCloseDetailsModal}
      />

      {/* Reject Claim Modal */}
      <Transition appear show={rejectModalOpen} as={Fragment}>
        <Dialog
          as="div"
          className="relative z-50"
          onClose={() => !rejectLoading && setRejectModalOpen(false)}
        >
          {/* Backdrop */}
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-black/30 backdrop-blur-sm" />
          </Transition.Child>

          {/* Dialog Container */}
          <div className="fixed inset-0 overflow-y-auto">
            <div className="flex min-h-full items-center justify-center p-4 text-center">
              <Transition.Child
                as={Fragment}
                enter="ease-out duration-300"
                enterFrom="opacity-0 scale-95"
                enterTo="opacity-100 scale-100"
                leave="ease-in duration-200"
                leaveFrom="opacity-100 scale-100"
                leaveTo="opacity-0 scale-95"
              >
                <Dialog.Panel className="w-full max-w-md transform overflow-hidden rounded-2xl bg-white p-6 text-left align-middle shadow-xl transition-all">
                  <div className="flex items-start gap-4">
                    {/* Icon */}
                    <div className="flex-shrink-0 rounded-full p-3 bg-red-100">
                      <ExclamationTriangleIcon className="h-6 w-6 text-red-600" />
                    </div>

                    {/* Content */}
                    <div className="flex-1 min-w-0">
                      <Dialog.Title
                        as="h3"
                        className="text-lg font-semibold text-gray-900 mb-2"
                      >
                        ยืนยันการปฏิเสธ
                      </Dialog.Title>
                      <div className="text-sm text-gray-600 mb-4">
                        ต้องการปฏิเสธคำขอสิทธิ์สำหรับบริษัท{' '}
                        <span className="font-semibold">"{selectedRejectClaim?.name}"</span>{' '}
                        หรือไม่?
                      </div>

                      {/* Textarea for reason */}
                      <div>
                        <label
                          htmlFor="reject-reason"
                          className="block text-sm font-medium text-gray-700 mb-2"
                        >
                          เหตุผลในการปฏิเสธ <span className="text-red-600">*</span>
                        </label>
                        <textarea
                          id="reject-reason"
                          rows={4}
                          className={`w-full px-3 py-2 border rounded-md focus:ring-2 focus:ring-red-500 focus:border-transparent ${
                            rejectError
                              ? 'border-red-300 bg-red-50'
                              : 'border-gray-300'
                          }`}
                          placeholder="กรุณาระบุเหตุผลที่ปฏิเสธคำขอนี้..."
                          value={rejectReason}
                          onChange={(e) => {
                            setRejectReason(e.target.value);
                            setRejectError('');
                          }}
                          disabled={rejectLoading}
                        />
                        {rejectError && (
                          <p className="mt-1 text-sm text-red-600">{rejectError}</p>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Actions */}
                  <div className="mt-6 flex gap-3 justify-end">
                    <button
                      type="button"
                      className="inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                      onClick={() => setRejectModalOpen(false)}
                      disabled={rejectLoading}
                    >
                      ยกเลิก
                    </button>
                    <button
                      type="button"
                      className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-red-600 hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                      onClick={handleRejectSubmit}
                      disabled={rejectLoading}
                    >
                      {rejectLoading ? (
                        <>
                          <FiRefreshCw className="animate-spin -ml-1 mr-2 h-4 w-4" />
                          กำลังปฏิเสธ...
                        </>
                      ) : (
                        <>
                          <FiXCircle className="-ml-1 mr-2 h-4 w-4" />
                          ยืนยันปฏิเสธ
                        </>
                      )}
                    </button>
                  </div>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition>

      {ConfirmDialog}
      {AlertDialog}
    </AdminLayout>
  );
};

export default ClaimsManagement;